import { createElement as rc, cloneElement, Fragment, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import useNavigationSelection from '../../hooks/useNavigationSelection';
import services from 'lib_ui-services';
import logger from '@sstdev/lib_logging';
import lodash from 'lodash';
const { camelCase } = lodash;
const { filterFactory } = services;

const _p = { useNavigationSelection };
export const _private = _p;

/**
 * @typedef {Object} Props
 * @property {Object} hNode
 * @property {string} currentRoute
 * @property {Array} children
 */
/** @type {import('react').FC<Props>} */
function NestedDetail(props) {
    const {
        hNode: { propertyName, filters },
        children = []
    } = props || { hNode: {} };

    // we should build the entire contextualInfo here, but for now, we just need the navigationSelection
    const navigationSelection = _p.useNavigationSelection();

    const filterWrapper = useCallback(
        (nested = {}) => {
            // if we don't have additional filters, we just need to prefix the propertyName to the nested filters
            if (!filters?.length) {
                return Object.entries(nested).reduce((acc, [key, value]) => {
                    if (['$and', '$or', '$nor'].includes(key) && Array.isArray(value)) {
                        // need to prefix each entry in the array
                        const nestedValues = value.map(v => {
                            if (typeof v === 'object' && v !== null) {
                                return Object.entries(v).reduce((obj, [k, val]) => {
                                    obj[`${propertyName}.${k}`] = val;
                                    return obj;
                                }, {});
                            }
                            return v;
                        });
                        acc[key] = nestedValues;
                    } else {
                        acc[`${propertyName}.${key}`] = value;
                    }
                    return acc;
                }, {});
            }
            // if we do have additional filters, we need to embed them all into a $elemMatch
            let otherReqs = {};
            for (const filterHNode of filters) {
                const filterName = camelCase(filterHNode.hNodeType);
                const filterStrategy = filterFactory(filterName);
                let filter = filterStrategy?.fromHNode?.(filterHNode, { navigationSelection });
                if (filter) {
                    otherReqs = { ...otherReqs, ...filterStrategy.getMql({ [filterName]: filter }) };
                } else {
                    logger.warn(`No filter strategy found for hNodeType: ${filterHNode.hNodeType}`);
                }
            }

            // make sure to use nested to override any requirements from otherReqs
            return {
                [propertyName]: { $elemMatch: { ...otherReqs, ...nested } }
            };
        },
        [filters, navigationSelection, propertyName]
    );

    //We are letting React render the nested elements first, so we don't need any logic (or references) to that
    //then, we clone, inject with our propertyPath, memoize and replace those elements.
    //(The memoization is so that on subsequent renders, our cloned and updated element is directly rendered.)
    const nestedSearchElements = useMemo(
        () =>
            children.map(element => {
                return cloneElement(element, {
                    ...element.props,
                    filterWrapper,
                    key: element.props.hNode?.id
                });
            }),
        [children, filterWrapper]
    );

    return rc(Fragment, null, ...nestedSearchElements);
}

NestedDetail.propTypes = { children: PropTypes.array.isRequired };

export default NestedDetail;
